import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';
import Logo from 'components/common/Logo';
import { post } from '../../api/index.js';

// ✅ Validation Schema
const schema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  dateOfBirth: yup
    .date()
    .typeError('Invalid date')
    .max(moment().subtract(18, 'year').toDate(), 'You must be at least 18 years old')
    .required('Date of birth is required'),

  email: yup.string().email('Invalid email').required('Email is required'),
  mobile: yup
    .string()
    .matches(/^[0-9]{10}$/, 'Mobile must be 10 digits')
    .required('Mobile number is required'),
  emergencyContact: yup
    .string()
    .matches(/^[0-9]{10}$/, 'Contact must be 10 digits')
    .required('Emergency contact is required'),
  permanentAddress: yup.string().required('Permanent address is required'),
  currentAddress: yup.string().required('Current address is required'),
  qualification: yup.string().required('Qualification is required'),
  prevCompany: yup.string(),
  noticePeriod: yup.string(),

  aadharCard: yup
    .mixed()
    .test('fileRequired', 'Aadhar card is required', (value) => value && value.length > 0)
    .test('fileSize', 'File is too large', (value) => {
      if (!value || value.length === 0) return true;
      return value[0].size <= 5 * 1024 * 1024; // 5MB
    }),
  degreeCertificate: yup
    .mixed()
    .test('fileRequired', 'Degree certificate is required', (value) => value && value.length > 0)
    .test('fileSize', 'File is too large', (value) => {
      if (!value || value.length === 0) return true;
      return value[0].size <= 5 * 1024 * 1024; // 5MB
    }),
  picture: yup
    .mixed()
    .test('fileRequired', 'Profile picture is required', (value) => value && value.length > 0)
    .test('fileSize', 'File is too large', (value) => {
      if (!value || value.length === 0) return true;
      return value[0].size <= 5 * 1024 * 1024; // 5MB
    }),
  resume: yup
    .mixed()
    .test('fileRequired', 'Resume is required', (value) => value && value.length > 0)
    .test('fileSize', 'File is too large', (value) => {
      if (!value || value.length === 0) return true;
      return value[0].size <= 5 * 1024 * 1024; // 5MB
    }),
  dosument1: yup
    .mixed()
    .nullable()
    .notRequired()
    .test('fileSize', 'File is too large', (value) => {
      if (!value || value.length === 0) return true; // ✅ optional
      return value[0].size <= 5 * 1024 * 1024; // 5MB
    }),

  document2: yup
    .mixed()
    .nullable()
    .notRequired()
    .test('fileSize', 'File is too large', (value) => {
      if (!value || value.length === 0) return true; // ✅ optional
      return value[0].size <= 5 * 1024 * 1024; // 5MB
    }),
});

export default function CandidateForm() {
  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      email: '',
      mobile: '',
      emergencyContact: '',
      permanentAddress: '',
      currentAddress: '',
      qualification: '',
      prevCompany: '',
      prevCTC: '',
      noticePeriod: '',
      aadharCard: null,
      degreeCertificate: null,
      picture: null,
      resume: null,
      document1: null,
      document2: null,
    },
  });

  const [preview, setPreview] = useState({
    picture: null,
    aadharCard: null,
    degreeCertificate: null,
    resume: null,
    document1: null,
    document2: null,
  });
  const [sameAddress, setSameAddress] = useState(false);

  const permanentAddress = watch('permanentAddress');

  useEffect(() => {
    if (sameAddress) {
      const timeout = setTimeout(() => {
        setValue('currentAddress', permanentAddress || '');
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [sameAddress, permanentAddress, setValue]);

  const handleFileChange = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      setPreview((prev) => ({
        ...prev,
        [field]: file,
      }));
    }
  };

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        // Handle FileList (from <input type="file" />)
        if (value instanceof FileList) {
          if (value.length > 0) {
            formData.append(key, value[0]); // only take the first file
          }
        }
        // Handle Date object (convert to string)
        else if (value instanceof Date) {
          formData.append(key, value.toISOString());
        }
        // Handle regular fields
        else {
          formData.append(key, value);
        }
      });

      const res = await post('common/create_candidate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status == 200) {
        enqueueSnackbar(res.message || 'Data Saved SuccessFully', {
          variant: 'success',
          autoHideDuration: 3000,
        });
        reset();
        setPreview({ picture: null, aadharCard: null, degreeCertificate: null });
      } else {
        enqueueSnackbar(res.message || 'Error While Saving Data Please Try Later', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error While Saving Data Please Try Later', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const handleReset = () => {
    reset();
    setPreview({ picture: null, aadharCard: null, degreeCertificate: null });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack mb={2}>
        <Logo />
      </Stack>
      <Paper sx={{ p: 2, borderRadius: 3 }} elevation={3}>
        <Typography variant="h5" fontWeight={600} mb={3}>
          Candidate Registration Form
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            {/* First Name */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    slotProps={{ htmlInput: { maxLength: 20 } }}
                  />
                )}
              />
            </Grid>

            {/* Last Name */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    slotProps={{ htmlInput: { maxLength: 20 } }}
                  />
                )}
              />
            </Grid>

            {/* Date of Birth */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    label="Date of Birth"
                    fullWidth
                    variant="filled"
                    error={!!errors.dateOfBirth}
                    helperText={errors.dateOfBirth?.message}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                      input: {
                        inputProps: {
                          max: moment().subtract(18, 'year').format('YYYY-MM-DD'), // 🔥 correct
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Email */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>

            {/* Mobile */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="mobile"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Mobile Number"
                    fullWidth
                    error={!!errors.mobile}
                    helperText={errors.mobile?.message}
                  />
                )}
              />
            </Grid>

            {/* Emergency Contact */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="emergencyContact"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Emergency Contact"
                    fullWidth
                    error={!!errors.emergencyContact}
                    helperText={errors.emergencyContact?.message}
                  />
                )}
              />
            </Grid>

            {/* Permanent Address */}
            <Grid item size={{ xs: 12, sm: 12 }}>
              <Controller
                name="permanentAddress"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Permanent Address (Full Address, City, State and Pin code)"
                    fullWidth
                    multiline
                    rows={2}
                    error={!!errors.permanentAddress}
                    helperText={errors.permanentAddress?.message}
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sameAddress}
                    onChange={(e) => setSameAddress(e.target.checked)}
                  />
                }
                label="Current address is same as Permanent address"
              />
            </Grid>
            {/* Current Address */}
            <Grid item size={{ xs: 12, sm: 12 }}>
              <Controller
                name="currentAddress"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Current Address (Full Address, City, State and Pin code)"
                    fullWidth
                    multiline
                    rows={2}
                    disabled={sameAddress}
                    error={!!errors.currentAddress}
                    helperText={errors.currentAddress?.message}
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                  />
                )}
              />
            </Grid>

            {/* Qualification */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="qualification"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Qualification"
                    fullWidth
                    error={!!errors.qualification}
                    helperText={errors.qualification?.message}
                    slotProps={{ htmlInput: { maxLength: 20 } }}
                  />
                )}
              />
            </Grid>

            {/* Previous Company */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="prevCompany"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Previous Company"
                    fullWidth
                    error={!!errors.qualification}
                    helperText={errors.qualification?.message}
                    slotProps={{ htmlInput: { maxLength: 50 } }}
                  />
                )}
              />
            </Grid>

            {/* Previous CTC */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="prevCTC"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Previous CTC"
                    fullWidth
                    error={!!errors.qualification}
                    helperText={errors.qualification?.message}
                    slotProps={{ htmlInput: { maxLength: 10 } }}
                  />
                )}
              />
            </Grid>

            {/* Previous CTC */}
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="noticePeriod"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notice Period (e.g 30d)"
                    fullWidth
                    error={!!errors.qualification}
                    helperText={errors.qualification?.message}
                    slotProps={{ htmlInput: { maxLength: 10 } }}
                  />
                )}
              />
            </Grid>

            {/* Picture Upload */}
            <Grid item size={{ xs: 12, sm: 3 }}>
              <Controller
                name="picture"
                control={control}
                render={({ field }) => (
                  <>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ textTransform: 'none' }}
                    >
                      {preview?.picture?.name || 'Upload Profile Picture'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          field.onChange(e.target.files);
                          handleFileChange('picture', e);
                        }}
                      />
                    </Button>

                    <Typography color="error" variant="caption">
                      {errors.picture?.message}
                    </Typography>
                  </>
                )}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 1 }}>
              {' '}
              {preview?.picture && (
                <Box>
                  <img
                    src={URL.createObjectURL(preview?.picture)}
                    alt="Profile"
                    width="40%"
                    style={{ borderRadius: 8 }}
                  />
                </Box>
              )}
            </Grid>
            {/* Resume */}
            <Grid item size={{ xs: 12, sm: 3 }}>
              <Controller
                name="resume"
                control={control}
                render={({ field }) => (
                  <>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ textTransform: 'none' }}
                    >
                      {preview?.resume?.name || 'Upload Your Resume'}
                      <input
                        type="file"
                        hidden
                        accept="application/pdf"
                        onChange={(e) => {
                          field.onChange(e.target.files);
                          handleFileChange('resume', e);
                        }}
                      />
                    </Button>
                    <Typography color="error" variant="caption">
                      {errors.resume?.message}
                    </Typography>
                  </>
                )}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 1 }}>
              {preview?.resume && (
                <Box>
                  {preview.resume.type == 'application/pdf' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => window.open(URL.createObjectURL(preview?.resume))}
                    >
                      <PictureAsPdfIcon color="error" sx={{ fontSize: 40 }} />
                    </Box>
                  ) : (
                    <img
                      src={URL.createObjectURL(preview?.resume)}
                      alt="Profile"
                      width="30%"
                      style={{ borderRadius: 8 }}
                    />
                  )}
                </Box>
              )}
            </Grid>

            {/* Degree Certificate */}
            <Grid item size={{ xs: 12, sm: 3 }}>
              <Controller
                name="degreeCertificate"
                control={control}
                render={({ field }) => (
                  <>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ textTransform: 'none' }}
                    >
                      {preview?.degreeCertificate?.name || 'Upload Degree Certificate'}
                      <input
                        type="file"
                        hidden
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          field.onChange(e.target.files);
                          handleFileChange('degreeCertificate', e);
                        }}
                      />
                    </Button>
                    <Typography color="error" variant="caption">
                      {errors.degreeCertificate?.message}
                    </Typography>
                  </>
                )}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 1 }}>
              {preview?.degreeCertificate && (
                <Box>
                  {preview.degreeCertificate.type == 'application/pdf' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => window.open(URL.createObjectURL(preview?.degreeCertificate))}
                    >
                      <PictureAsPdfIcon color="error" sx={{ fontSize: 40 }} />
                      {/* <Typography variant="caption">{preview?.degreeCertificate?.name}</Typography> */}
                    </Box>
                  ) : (
                    <img
                      src={URL.createObjectURL(preview?.degreeCertificate)}
                      alt="Profile"
                      width="30%"
                      style={{ borderRadius: 8 }}
                    />
                  )}
                  {/* URL.createObjectURL(file) */}
                </Box>
              )}
            </Grid>
            {/* Aadhar Upload */}
            <Grid item size={{ xs: 12, sm: 3 }}>
              <Controller
                name="aadharCard"
                control={control}
                render={({ field }) => (
                  <>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ textTransform: 'none' }}
                    >
                      {preview?.aadharCard?.name || 'Upload Aadhar Card'}
                      <input
                        type="file"
                        hidden
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          field.onChange(e.target.files);
                          handleFileChange('aadharCard', e);
                        }}
                      />
                    </Button>

                    <Typography color="error" variant="caption">
                      {errors.aadharCard?.message}
                    </Typography>
                  </>
                )}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 1 }}>
              {preview?.aadharCard && (
                <Box>
                  {preview?.aadharCard?.type == 'application/pdf' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => window.open(URL.createObjectURL(preview?.aadharCard))}
                    >
                      <PictureAsPdfIcon color="error" sx={{ fontSize: 40 }} />
                      {/* <Typography variant="caption">{preview.aadharCard.name}</Typography> */}
                    </Box>
                  ) : (
                    <img
                      src={URL.createObjectURL(preview?.aadharCard)}
                      alt="Profile"
                      width="50%"
                      style={{ borderRadius: 8 }}
                      onClick={() => window.open(URL.createObjectURL(preview?.aadharCard))}
                    />
                  )}
                </Box>
              )}
            </Grid>
            {/* Document1 */}
            <Grid item size={{ xs: 12, sm: 3 }}>
              <Controller
                name="document1"
                control={control}
                render={({ field }) => (
                  <>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ textTransform: 'none' }}
                    >
                      {preview?.document1?.name || 'Document1 (Optional)'}
                      <input
                        type="file"
                        hidden
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          field.onChange(e.target.files);
                          handleFileChange('document1', e);
                        }}
                      />
                    </Button>
                    <Typography color="error" variant="caption">
                      {errors.document1?.message}
                    </Typography>
                  </>
                )}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 1 }}>
              {preview?.document1 && (
                <Box>
                  {preview.document1.type == 'application/pdf' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => window.open(URL.createObjectURL(preview?.document1))}
                    >
                      <PictureAsPdfIcon color="error" sx={{ fontSize: 40 }} />
                    </Box>
                  ) : (
                    <img
                      src={URL.createObjectURL(preview?.document1)}
                      alt="Profile"
                      width="30%"
                      style={{ borderRadius: 8 }}
                    />
                  )}
                </Box>
              )}
            </Grid>
            {/* Document1 */}
            <Grid item size={{ xs: 12, sm: 3 }}>
              <Controller
                name="document2"
                control={control}
                render={({ field }) => (
                  <>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{ textTransform: 'none' }}
                    >
                      {preview?.document2?.name || 'Document2 (Optional)'}
                      <input
                        type="file"
                        hidden
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          field.onChange(e.target.files);
                          handleFileChange('document2', e);
                        }}
                      />
                    </Button>
                    <Typography color="error" variant="caption">
                      {errors.document2?.message}
                    </Typography>
                  </>
                )}
              />
            </Grid>
            <Grid item size={{ xs: 12, sm: 1 }}>
              {preview?.document2 && (
                <Box>
                  {preview.document2.type == 'application/pdf' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => window.open(URL.createObjectURL(preview?.document2))}
                    >
                      <PictureAsPdfIcon color="error" sx={{ fontSize: 40 }} />
                    </Box>
                  ) : (
                    <img
                      src={URL.createObjectURL(preview?.document2)}
                      alt="Profile"
                      width="30%"
                      style={{ borderRadius: 8 }}
                    />
                  )}
                </Box>
              )}
            </Grid>

            {/* Submit + Reset */}
            <Grid item size={{ xs: 12 }}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button type="reset" variant="outlined" color="secondary" onClick={handleReset}>
                  Reset
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  Submit
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}
